import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useNotifications from '../../../hooks/useNotifications'
import { TreeContext } from '../TreeContextProvider'
import useTreePersons from '../useTreePersons'
import { getBackendBaseUrl } from '../../../util/UrlUtil'
import useStreamDownload from '../../../hooks/useStreamDownload'
import { getTreeGroupMembers } from '../../../services/treeService'
import PersonsResponsiveTable from './PersonsResponsiveTable'
import PersonsTableFilter from './PersonsTableFilter'
import { createTableHeaders } from './usePersonsTableHeaders'
import { usePersonsTableSorting } from './usePersonsTableSorting'

export default function PersonsTable({ treeId }) {
  const { t } = useTranslation()
  const { error } = useNotifications()
  const { state } = useContext(TreeContext)
  const { personsState, personsActions } = useTreePersons(treeId)
  const [filter, setFilter] = useState(personsState.nameFilter)
  const [groupsMembers, setGroupMembers] = useState()
  const { streamDataDownload } = useStreamDownload()
  const MAX_PERSONS_DOWNLOAD = 100000

  // Use the extracted sorting hook
  const {
    sortField,
    sortAsc,
    genderSortFieldIndex,
    lifespanSortFieldIndex,
    genderSortFields,
    lifespanSortFields,
    sortByFieldType,
    resetResults,
  } = usePersonsTableSorting(personsActions)

  useEffect(() => {
    // only load these in the beginning
    if (treeId) {
      getTreeGroupMembers(treeId)
        .then((groupMembersResponse) => {
          setGroupMembers(groupMembersResponse.data)
        })
        .catch((err) => {
          error(
            `${t('tree.error.groupMembers', 'Error getting group members for tree')} ${treeId}: ${err.message || ''}`,
            err
          )
        })
    }
  }, [error, t, treeId])

  // Create table headers using the extracted function
  const TableHeaders = createTableHeaders({
    t,
    sortField,
    sortAsc,
    genderSortFieldIndex,
    lifespanSortFieldIndex,
    genderSortFields,
    lifespanSortFields,
    sortByFieldType,
  })

  const getUserName = (cisId) => {
    return groupsMembers?.members?.find((member) => {
      return member.memberCisId === cisId
    })?.contactName
  }

  const downloadPersonsInTree = () => {
    streamDataDownload({
      requestPath: `${getBackendBaseUrl()}/tree/${treeId}/dump`,
    })
  }

  return (
    <>
      <h4>{t('tree.persons', 'Persons')}</h4>
      {personsState.persons?.persons && (
        <>
          <PersonsTableFilter
            filter={filter}
            setFilter={setFilter}
            resetResults={resetResults}
            personsActions={personsActions}
            downloadPersonsInTree={downloadPersonsInTree}
            isDownloadDisabled={state.tree.personCount > MAX_PERSONS_DOWNLOAD}
            showDownloadLimit={state.tree.personCount > MAX_PERSONS_DOWNLOAD}
            t={t}
          />
          <PersonsResponsiveTable
            personsState={personsState}
            personsActions={personsActions}
            state={state}
            getUserName={getUserName}
            t={t}
            TableHeaders={TableHeaders}
          />
        </>
      )}
      {!personsState.persons?.persons && <p>{t('tree.noPersons', 'No persons in the tree')}</p>}
    </>
  )
}
